// src/firebase.js
import { initializeApp, getApps, FirebaseApp, deleteApp } from 'firebase/app'
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging'
import { getAnalytics, setAnalyticsCollectionEnabled, Analytics } from 'firebase/analytics'
import FirebasetokenManager from '@/utils/firebasetokenManager'
import { getFirebaseConfig, getFirebaseEnvConfig, validateFirebaseConfig } from '@/config/firebase'

// Firebase 服务状态管理
interface FirebaseServiceState {
  app: FirebaseApp | null;
  messaging: Messaging | null;
  analytics: Analytics | null;
  isInitialized: boolean;
  isMessagingInitialized: boolean;
  isAnalyticsInitialized: boolean;
}

// 全局状态
let firebaseState: FirebaseServiceState = {
  app: null,
  messaging: null,
  analytics: null,
  isInitialized: false,
  isMessagingInitialized: false,
  isAnalyticsInitialized: false
};

/**
 * 获取当前 Firebase 配置（动态获取，支持接口更新）
 */
const getCurrentFirebaseConfig = () => {
  return getFirebaseConfig()
}

/**
 * 检查环境是否支持 Firebase 服务
 */
const isEnvironmentSupported = (): boolean => {
  return (
    location.protocol.startsWith('https') ||
    location.protocol.startsWith('wss') ||
    location.hostname.startsWith('localhost')
  );
};

/**
 * 验证 Firebase 配置是否有效
 */
const validateFirebaseConfiguration = (): boolean => {
  try {
    const config = getCurrentFirebaseConfig();
    const requiredFields = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
    const missingFields = requiredFields.filter(field => !config[field as keyof typeof config]);

    if (missingFields.length > 0) {
      console.error('Firebase 配置缺失字段:', missingFields);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Firebase 配置验证失败:', error);
    return false;
  }
};

/**
 * 初始化 Firebase 应用核心
 */
export const initializeFirebaseApp = (): FirebaseApp | null => {
  if (firebaseState.isInitialized && firebaseState.app) {
    console.log('Firebase 应用已经初始化，返回现有实例');
    return firebaseState.app;
  }

  if (!isEnvironmentSupported()) {
    console.warn('当前环境不支持 Firebase 服务');
    return null;
  }

  if (!validateFirebaseConfiguration()) {
    console.error('Firebase 配置验证失败，无法初始化');
    return null;
  }

  try {
    // 检查是否已经初始化
    const existingApps = getApps();
    if (existingApps.length > 0) {
      firebaseState.app = existingApps[0];
      console.log('使用现有的 Firebase 应用实例');
    } else {
      const firebaseConfig = getCurrentFirebaseConfig();
      firebaseState.app = initializeApp(firebaseConfig);
      console.log('创建新的 Firebase 应用实例');
    }

    firebaseState.isInitialized = true;
    return firebaseState.app;
  } catch (error) {
    console.error('Firebase 应用初始化失败:', error);
    return null;
  }
};

/**
 * 初始化 Firebase Messaging 服务
 */
export const initializeFirebaseMessaging = (): Messaging | null => {
  if (firebaseState.isMessagingInitialized && firebaseState.messaging) {
    console.log('Firebase Messaging 已经初始化，返回现有实例');
    return firebaseState.messaging;
  }

  const app = firebaseState.app || initializeFirebaseApp();
  if (!app) {
    console.error('Firebase 应用未初始化，无法初始化 Messaging');
    return null;
  }

  try {
    firebaseState.messaging = getMessaging(app);
    firebaseState.isMessagingInitialized = true;
    return firebaseState.messaging;
  } catch (error) {
    console.error('Firebase Messaging 初始化失败:', error);
    return null;
  }
};

/**
 * 初始化 Firebase Analytics 服务
 */
export const initializeFirebaseAnalytics = (): Analytics | null => {
  if (firebaseState.isAnalyticsInitialized && firebaseState.analytics) {
    console.log('Firebase Analytics 已经初始化，返回现有实例');
    return firebaseState.analytics;
  }

  const app = firebaseState.app || initializeFirebaseApp();
  if (!app) {
    console.error('Firebase 应用未初始化，无法初始化 Analytics');
    return null;
  }

  try {
    firebaseState.analytics = getAnalytics(app);
    setAnalyticsCollectionEnabled(firebaseState.analytics, true);
    firebaseState.isAnalyticsInitialized = true;
    return firebaseState.analytics;
  } catch (error) {
    console.error('Firebase Analytics 初始化失败:', error);
    return null;
  }
};

/**
 * 完整初始化 Firebase 服务（应用 + Messaging + Analytics）
 */
export const initializeFirebaseServices = async (): Promise<{
  app: FirebaseApp | null;
  messaging: Messaging | null;
  analytics: Analytics | null;
  success: boolean;
}> => {
  try {
    console.log('开始初始化 Firebase 服务...');

    // 1. 初始化应用
    const app = initializeFirebaseApp();
    if (!app) {
      console.error('Firebase 应用初始化失败');
      return { app: null, messaging: null, analytics: null, success: false };
    }

    // 2. 初始化 Messaging
    const messaging = initializeFirebaseMessaging();

    // 3. 初始化 Analytics
    const analytics = initializeFirebaseAnalytics();

    const success = !!(app && (messaging || analytics));
    console.log('Firebase 服务初始化完成:', {
      app: !!app,
      messaging: !!messaging,
      analytics: !!analytics,
      success
    });

    return { app, messaging, analytics, success };
  } catch (error) {
    console.error('Firebase 服务初始化失败:', error);
    return { app: null, messaging: null, analytics: null, success: false };
  }
};


/**
 * 获取 Firebase 应用实例
 */
export const getFirebaseApp = (): FirebaseApp | null => {
  return firebaseState.app || initializeFirebaseApp();
};

/**
 * 获取 Firebase Messaging 实例
 */
export const getFirebaseMessaging = (): Messaging | null => {
  return firebaseState.messaging || initializeFirebaseMessaging();
};

/**
 * 获取 Firebase Analytics 实例
 */
export const getFirebaseAnalytics = (): Analytics | null => {
  return firebaseState.analytics || initializeFirebaseAnalytics();
};

/**
 * 检查 Firebase 服务状态
 */
export const getFirebaseServiceStatus = () => {
  return {
    isAppInitialized: firebaseState.isInitialized,
    isMessagingInitialized: firebaseState.isMessagingInitialized,
    isAnalyticsInitialized: firebaseState.isAnalyticsInitialized,
    hasApp: !!firebaseState.app,
    hasMessaging: !!firebaseState.messaging,
    hasAnalytics: !!firebaseState.analytics,
    isEnvironmentSupported: isEnvironmentSupported(),
    isConfigurationValid: validateFirebaseConfiguration()
  };
};

/**
 * 重置 Firebase 服务（用于测试）
 */
export const resetFirebaseServices = () => {
  if (firebaseState.app) {
    deleteApp(firebaseState.app);
  }

  firebaseState = {
    app: null,
    messaging: null,
    analytics: null,
    isInitialized: false,
    isMessagingInitialized: false,
    isAnalyticsInitialized: false
  };

  console.log('Firebase 服务已重置');
};

/**
 * 获取 FCM Token
 */
export const getFCMToken = async (): Promise<string | null> => {
  const messaging = getFirebaseMessaging();
  if (!messaging) {
    console.error('Firebase Messaging 未初始化，无法获取 Token');
    return null;
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.warn('通知权限未授予');
      return null;
    }

    const envConfig = getFirebaseEnvConfig();
    const token = await getToken(messaging, {
      vapidKey: envConfig.vapidKey
    });

    if (token) {
      // 更新 Token 管理器
      const config = getCurrentFirebaseConfig();
      FirebasetokenManager.setToken(token);
      FirebasetokenManager.setFirebaseProjectId(config.projectId);
      FirebasetokenManager.setUpdateToken(true);
      return token;
    } else {
      console.warn('FCM Token 获取失败');
      return null;
    }
  } catch (error) {
    console.error('获取 FCM Token 失败:', error);
    return null;
  }
};

/**
 * 监听前台消息
 */
export const listenForForegroundMessages = (callback: (payload: any) => void) => {
  const messaging = getFirebaseMessaging();
  if (!messaging) {
    console.error('Firebase Messaging 未初始化，无法监听消息');
    return null;
  }

  try {
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('收到前台消息:', payload);
      callback(payload);
    });

    console.log('前台消息监听器已设置');
    return unsubscribe;
  } catch (error) {
    console.error('设置前台消息监听器失败:', error);
    return null;
  }
};

/**
 * 平台消息处理（兼容性保留）
 */
export function onPlatformMessage(message: string) {
  const eventData = JSON.parse(message);
  if (eventData.command === 'onNewToken') {
    const projectId = eventData.data.projectId;
    const token = eventData.data.token;

    // 更新配置中的 projectId（通过配置管理器）
    const currentConfig = getCurrentFirebaseConfig();
    if (currentConfig) {
      currentConfig.projectId = projectId;
    }
    FirebasetokenManager.setToken(token);
    FirebasetokenManager.setFirebaseProjectId(projectId);
    FirebasetokenManager.setUpdateToken(true);
  }
}

/**
 * 兼容性函数（保持向后兼容）
 */
export const initializeFirebase = initializeFirebaseApp;
export const initializeMessaging = initializeFirebaseMessaging;
export const initializeAnalytics = initializeFirebaseAnalytics;
export const getMessagingInstance = getFirebaseMessaging;
export const getAnalyticsInstance = getFirebaseAnalytics;
export const isFirebaseInitialized = () => firebaseState.isInitialized;
export const resetFirebase = resetFirebaseServices;

/**
 * 兼容性 useFirebase 函数
 */
export const useFirebase = () => {
  if (!isEnvironmentSupported()) {
    console.warn('当前环境不支持 Firebase 服务');
    return null;
  }

  const app = getFirebaseApp();
  if (!app) {
    console.error('Firebase 初始化失败');
    return null;
  }

  const messaging = getFirebaseMessaging();
  if (!messaging) {
    console.error('Firebase Cloud Messaging 初始化失败');
    return null;
  }

  return {
    messaging,
    requestPermissionAndGetToken: getFCMToken,
    listenForMessages: listenForForegroundMessages
  };
};




